import { useEffect, useRef, useState, type ReactNode } from "react";
import { z } from "zod";
import { apiRequest, getAuthHeaders } from "@/lib/queryClient";

type CheckState = "notrun" | "loading" | "results" | "empty" | "incomplete";
type RoleLimit = { role: string; message: string };
const day = 86_400_000;

export function asAtState(asAt: string, now = Date.now()): "fresh" | "aging" | "stale" {
  const age = now - Date.parse(asAt);
  return age >= 7 * day ? "stale" : age >= day ? "aging" : "fresh";
}

export function CheckPanel({ id, number, title, purpose, state, total, asAt, onRun, disabled,
  roleLimit, children, loadingBody, incompleteBody, note }: {
  id: string; number: number; title: string; purpose: ReactNode; state: CheckState;
  total?: number; asAt?: string; onRun?: () => void; disabled?: boolean;
  roleLimit?: RoleLimit; children?: ReactNode;
  loadingBody?: ReactNode; incompleteBody?: ReactNode; note?: string;
}) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const busy = state === "loading";
  const incomplete = state === "incomplete";
  const freshness = asAt ? asAtState(asAt, now) : "fresh";
  return <section className="crmx-check-panel" data-testid={`check-${id}`} data-state={state} aria-busy={busy} aria-labelledby={`${id}-title`}>
    <div className="crmx-check-heading">
      <div><span className="crmx-settings-kicker">Check {number} of 3 · read-only</span>
        <h2 id={`${id}-title`}>{title}</h2><p className="crmx-check-purpose">{purpose}</p>
      </div>
      <div className="crmx-check-actions">
        <div className="crmx-check-count"><span className="crmx-settings-kicker">{roleLimit ? "Rows you can see" : "Rows"}</span>
          {busy ? <span className="crmx-check-skeleton" aria-label="Count loading" />
            : <span className={`crmx-check-value ${incomplete ? "is-unknown" : state === "notrun" ? "is-notrun" : ""}`}>
              {incomplete ? "unknown" : state === "notrun" ? "Not run" : total ?? "unknown"}
            </span>}
        </div>
        <button type="button" className="crmx-check-button" data-testid={`run-${id}`} onClick={onRun} disabled={disabled || busy}>
          {busy ? "Running check" : "Run check"}
        </button>
      </div>
    </div>
    <div className={`crmx-check-asat is-${freshness}`}>
      {busy ? "Running check" : asAt ? <>As at {new Date(asAt).toLocaleString("en-GB", {
        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      })}
        {incomplete ? " · incomplete" : freshness === "stale" ? " · stale · run again" : freshness === "aging" ? " · aging" : " · fresh"}</> : "Not run yet"}
    </div>
    {note && state !== "notrun" && <p className="crmx-check-note">{note}</p>}
    <div className="crmx-check-body" aria-live="polite">
      {roleLimit && <div className="crmx-check-role-limit"><b>Role limit</b>
        <span>Some rows may be hidden by your role ({roleLimit.role}). {roleLimit.message}</span></div>}
      {state === "notrun" ? <p className="crmx-check-neutral">Not run yet.</p>
        : busy ? loadingBody ?? <div className="crmx-check-skeleton-rows" aria-label="Loading rows">{[0, 1, 2].map(row =>
          <div key={row}>{[0, 1, 2].map(cell => <span key={cell} className="crmx-check-skeleton" />)}</div>)}</div>
          : incomplete ? incompleteBody ?? <div className="crmx-check-incomplete"><b>Check incomplete</b>
            <span>The check stopped before finishing. Rows and count are unknown. Run the check again.</span></div>
            : children}
    </div>
  </section>;
}

const reasonSchema = z.enum(["cap reached", "timed out", "error", "store missing"]);
const sourceRowSchema = z.object({ key: z.string(), lastModified: z.string().nullable(), reason: z.enum(["no record", "record with no file"]) });
const folderSchema = <T extends z.ZodTypeAny>(row: T) => z.object({
  status: z.enum(["complete", "incomplete"]), total: z.number().int().nonnegative().nullable(), scannedCount: z.number().int().nonnegative().nullable(),
  rows: z.array(row), reason: reasonSchema.optional(),
});
const storageSchema = z.object({
  status: z.enum(["complete", "incomplete"]), asAt: z.string().datetime(),
  total: z.number().int().nonnegative().nullable(), page: z.number().int().positive(), pageSize: z.literal(50),
  source: folderSchema(sourceRowSchema).extend({ outsideOrganisationCount: z.number().int().nonnegative().nullable() }),
  extraction: z.object({ status: z.enum(["complete", "incomplete"]), total: z.number().int().nonnegative().nullable(), scannedCount: z.number().int().nonnegative().nullable(), reason: reasonSchema.optional() }).strict(),
  roleLimit: z.object({ role: z.string(), message: z.string() }).nullable(),
}).superRefine((value, ctx) => {
  if ((value.status === "complete" && (value.total === null || value.source.status !== "complete" || value.extraction.status !== "complete"))
    || [value.source, value.extraction].some(folder => folder.status === "complete" && (folder.total === null || folder.scannedCount === null))
    || (value.source.status === "complete" && value.source.outsideOrganisationCount === null)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Incomplete check result" });
  }
});
const disposalSchema = z.object({
  status: z.literal("complete"), asAt: z.string().datetime(), total: z.number().int().nonnegative(),
  page: z.number().int().positive(), pageSize: z.literal(50),
  rows: z.array(z.object({ id: z.string(), sourceTable: z.string(), documentId: z.string(), locator: z.string(),
    deletedAt: z.string(), deletedByName: z.string().nullable() })),
  roleLimit: z.object({ role: z.string(), message: z.string() }).nullable(),
});

function useStoredCheck<T extends { status: "complete" | "incomplete"; total: number | null; asAt: string }>(path: string, schema: z.ZodType<T>) {
  const [state, setState] = useState<CheckState>("notrun");
  const [result, setResult] = useState<T>();
  const [asAt, setAsAt] = useState<string>();
  const generation = useRef(0);
  const active = useRef<AbortController>();
  useEffect(() => () => { generation.current += 1; active.current?.abort(); }, []);
  async function run(page = 1) {
    active.current?.abort();
    const controller = new AbortController();
    active.current = controller;
    const current = ++generation.current;
    setState("loading"); setResult(undefined);
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const response = await Promise.race([
        (async () => {
          const headers = await getAuthHeaders();
          controller.signal.throwIfAborted();
          const res = await fetch(`/api/settings/data-hygiene/${path}?page=${page}`, { method: "GET", headers, credentials: "include", signal: controller.signal });
          if (!res.ok) throw new Error("Check incomplete");
          return res.json();
        })(),
        new Promise<never>((_resolve, reject) => { timer = setTimeout(() => {
          controller.abort();
          reject(new Error("Check timed out"));
        }, 65_000); }),
      ]);
      const data = schema.parse(response);
      if (current !== generation.current) return;
      setResult(data); setAsAt(data.asAt);
      setState(data.status === "incomplete" ? "incomplete" : data.total === 0 ? "empty" : "results");
    } catch {
      if (current !== generation.current) return;
      setAsAt(new Date().toISOString()); setState("incomplete");
    } finally { clearTimeout(timer); }
  }
  return { state, result, asAt, run };
}

function Incomplete({ reason = "error" }: { reason?: string }) {
  return <div className="crmx-check-incomplete"><b>Check incomplete · {reason}</b>
    <span>Rows and count are unknown. Run the check again.</span></div>;
}

function CheckPaging({ page, total, onPage, label }: { page: number; total: number; onPage: (page: number) => void; label: string }) {
  return <nav className="crmx-check-paging" aria-label={label}>
    <button className="crmx-check-button" disabled={page === 1} onClick={() => onPage(page - 1)}>Previous page</button>
    <span>Page {page} of {Math.max(1, Math.ceil(total / 50))}</span>
    <button className="crmx-check-button" disabled={page * 50 >= total} onClick={() => onPage(page + 1)}>Next page</button>
  </nav>;
}

const keyCellStyle = { fontFamily: "var(--font-mono)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } as const;
const dateColumnStyle = { width: "calc(var(--space-12) * 4)" };
const shortColumnStyle = { width: "calc(var(--space-8) * 4)" };
function tableDate(value: string) {
  return new Date(value).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).replace(",", "");
}
function StorageBlocks({ result, loading = false }: { result?: z.infer<typeof storageSchema>; loading?: boolean }) {
  const source = result?.source;
  const extraction = result?.extraction;
  const skeleton = <div className="crmx-check-skeleton-rows" aria-label="Loading rows">{[0, 1, 2].map(row =>
    <div key={row}>{[0, 1, 2].map(cell => <span key={cell} className="crmx-check-skeleton" />)}</div>)}</div>;
  return <>
    <section className="crmx-check-group">
      <div className="crmx-check-group-heading"><h3 style={{ fontFamily: "var(--font-mono)" }}>financial-source-documents/</h3>
        {!loading && <span>{source?.status === "complete" ? `${source.total} not matched` : "unknown"}</span>}</div>
      {!loading && <p className="crmx-check-note">{`${source?.status === "complete" ? source.scannedCount : "unknown"} files checked`}</p>}
      <p className="crmx-check-note">Not matched in your organisation — not proof of an orphan and not a basis for deletion on its own.</p>
      {loading ? skeleton : source?.status !== "complete" ? <Incomplete reason={source?.reason} />
        : source.total === 0 ? <p className="crmx-check-neutral">No files without a record</p>
          : <div className="crmx-check-table-wrap"><table className="crmx-check-table">
            <colgroup><col /><col style={dateColumnStyle} /><col style={dateColumnStyle} /></colgroup><thead><tr>
            <th scope="col">Object key</th><th scope="col">Last modified</th><th scope="col">Reason</th>
          </tr></thead><tbody>{source.rows.map(row => <tr key={row.key}>
            <td style={keyCellStyle} title={row.key}>{row.key}</td>
            <td style={{ fontFamily: "var(--font-mono)" }}>{row.lastModified ? tableDate(row.lastModified) : "No file"}</td>
            <td>{row.reason}</td></tr>)}</tbody></table></div>}
      {!loading && <p className="crmx-check-note">Files outside your organisation or unknown: <b>
        {source?.status === "complete" ? source.outsideOrganisationCount : "unknown"}</b> · keys not shown</p>}
    </section>
    <section className="crmx-check-group">
      <div className="crmx-check-group-heading"><h3 style={{ fontFamily: "var(--font-mono)" }}>financial-extraction/</h3>
        {!loading && <span>{extraction?.status === "complete" ? `${extraction.total} past limit` : "unknown"}</span>}</div>
      {!loading && <p className="crmx-check-note">{`${extraction?.status === "complete" ? extraction.scannedCount : "unknown"} files checked`}</p>}
      <p className="crmx-check-note">Pending uploads are kept 30 minutes; provider copies 7 days.</p>
      {loading ? skeleton : extraction?.status !== "complete" ? <Incomplete reason={extraction?.reason} />
        : extraction.total === 0 ? <p className="crmx-check-neutral">No files past their retention limit</p>
          : <p className="crmx-check-neutral">Files past their retention limit: {extraction.total}</p>}
      <p className="crmx-check-note">Count across the store. Organisation ownership is unknown; keys are not shown.</p>
    </section>
  </>;
}

function StoragePanel() {
  const { state, result, asAt, run } = useStoredCheck("storage", storageSchema);
  const completedTotal = result?.source.total ?? 0;
  const body = <><StorageBlocks result={result} />
    {result && (result.status === "complete" || completedTotal > 50 || result.page > 1) &&
      <CheckPaging page={result.page} total={completedTotal} onPage={page => void run(page)} label="Stored files pages" />}
  </>;
  return <CheckPanel id="files" number={1} title="Stored files with no record" state={state}
    purpose="Each row is a stored file with no matching record, or a record whose file is missing. Review each one before anything is deleted."
    total={result?.total ?? undefined} asAt={asAt} roleLimit={result?.roleLimit ?? undefined} onRun={() => void run()}
    loadingBody={<StorageBlocks loading />} incompleteBody={body}>
    {body}
  </CheckPanel>;
}

function DisposalsPanel() {
  const { state, result, asAt, run } = useStoredCheck("disposals", disposalSchema);
  return <CheckPanel id="deleted" number={2} title="Deleted documents awaiting disposal" state={state}
    purpose="Each row is a document someone deleted. Its file or link has not been disposed of yet."
    note="Compliance, deal and entity rows hold a link, not a stored file."
    total={result?.total} asAt={asAt} roleLimit={result?.roleLimit ?? undefined} onRun={() => void run()}>
    {state === "empty" ? <p className="crmx-check-neutral">No deleted documents awaiting disposal</p> : result && <>
      <div className="crmx-check-table-wrap"><table className="crmx-check-table">
        <colgroup><col style={dateColumnStyle} /><col style={shortColumnStyle} /><col /><col style={dateColumnStyle} /><col style={shortColumnStyle} /></colgroup><thead><tr>
        {["Source table", "Document id", "Locator", "Deleted", "Deleted by"].map(heading => <th scope="col" key={heading}>{heading}</th>)}
      </tr></thead><tbody>{result.rows.map(row => <tr key={row.id}>
        <td style={keyCellStyle} title={row.sourceTable}>{row.sourceTable}</td><td style={keyCellStyle} title={row.documentId}>{row.documentId}</td>
        <td style={keyCellStyle} title={row.locator}>{row.locator}</td><td style={{ fontFamily: "var(--font-mono)" }}>{tableDate(row.deletedAt)}</td>
        <td>{row.deletedByName ?? "Name not available"}</td>
      </tr>)}</tbody></table></div>
      <CheckPaging page={result.page} total={result.total} onPage={page => void run(page)} label="Deleted documents pages" />
    </>}
  </CheckPanel>;
}

const linkSchema = z.object({
  kind: z.enum(["entity", "guarantor"]), linkId: z.string(), dealId: z.string(), dealName: z.string(),
  dealGroupId: z.string().nullable(), dealGroupName: z.string().nullable(),
  partyName: z.string().nullable(), partyGroupName: z.string().nullable(),
  reason: z.enum(["outside client group", "party not visible", "deal has no client group"]),
});
const resultSchema = z.object({
  status: z.literal("complete"), asAt: z.string().datetime(), total: z.number().int().nonnegative(),
  page: z.number().int().positive(), pageSize: z.literal(50), rows: z.array(linkSchema),
  roleLimit: z.object({ role: z.string(), message: z.string() }).nullable(),
});
type CheckResult = z.infer<typeof resultSchema>;
type AnalysisLink = z.infer<typeof linkSchema>;

function LinkTable({ title, rows, party, ungrouped = false }: { title: string; rows: AnalysisLink[]; party: string; ungrouped?: boolean }) {
  const headings = ungrouped ? ["Deal", "Link", "Party", "Party's group", "Reason"]
    : ["Deal", party, `${party}'s group`, "Deal's group", "Reason"];
  return <section className="crmx-check-group">
    <div className="crmx-check-group-heading"><h3>{title}</h3><span>{rows.length} on this page</span></div>
    {rows.length === 0 ? <p className="crmx-check-neutral">No rows on this page</p> : <div className="crmx-check-table-wrap">
      <table className="crmx-check-table"><thead><tr>
        {headings.map(heading => <th key={heading} scope="col">{heading}</th>)}
      </tr></thead><tbody>{rows.map(row => <tr key={`${row.kind}-${row.linkId}`}>
        <td>{row.dealName}</td>{ungrouped && <td>{row.kind === "entity" ? "Entity" : "Guarantor"}</td>}
        <td>{row.partyName ?? "Not visible to your role"}</td>
        <td>{row.partyGroupName ?? "Not visible to your role"}</td>
        {!ungrouped && <td>{row.dealGroupName ?? "Not visible to your role"}</td>}
        <td>{row.reason}</td>
      </tr>)}</tbody></table></div>}
  </section>;
}

export default function DataHygiene() {
  const [state, setState] = useState<CheckState>("notrun");
  const [result, setResult] = useState<CheckResult>();
  const [asAt, setAsAt] = useState<string>();
  const generation = useRef(0);
  useEffect(() => () => { generation.current += 1; }, []);

  async function run(page = 1) {
    const current = ++generation.current;
    setState("loading");
    setResult(undefined);
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const response = await Promise.race([
        apiRequest("GET", `/api/settings/data-hygiene/analysis-links?page=${page}`).then(res => res.json()),
        new Promise<never>((_resolve, reject) => { timer = setTimeout(() => reject(new Error("Check timed out")), 60_000); }),
      ]);
      const data = resultSchema.parse(response);
      if (current !== generation.current) return;
      setResult(data); setAsAt(data.asAt); setState(data.total === 0 ? "empty" : "results");
    } catch {
      if (current !== generation.current) return;
      setAsAt(new Date().toISOString()); setState("incomplete");
    } finally { clearTimeout(timer); }
  }

  return <div className="crmx-check-panels">
    <StoragePanel />
    <DisposalsPanel />
    <CheckPanel id="links" number={3} title="Financial-analysis links outside the deal's client group" state={state}
      purpose="Each row links a deal's financial analysis to an account or person outside the deal's client group. Review each link."
      total={result?.total} asAt={asAt} roleLimit={result?.roleLimit ?? undefined} onRun={() => void run()}>
      {state === "empty" ? <p className="crmx-check-neutral">No links outside the deal's client group</p> : result && <>
        <LinkTable title="Entity links" party="Account" rows={result.rows.filter(row => row.kind === "entity" && row.dealGroupId !== null)} />
        <LinkTable title="Guarantor links" party="Contact" rows={result.rows.filter(row => row.kind === "guarantor" && row.dealGroupId !== null)} />
        <LinkTable title="Deals with no client group" party="Party" ungrouped rows={result.rows.filter(row => row.dealGroupId === null)} />
        <nav className="crmx-check-paging" aria-label="Check results pages">
          <button className="crmx-check-button" disabled={result.page === 1} onClick={() => void run(result.page - 1)}>Previous page</button>
          <span>Page {result.page} of {Math.max(1, Math.ceil(result.total / result.pageSize))}</span>
          <button className="crmx-check-button" disabled={result.page * result.pageSize >= result.total} onClick={() => void run(result.page + 1)}>Next page</button>
        </nav>
      </>}
    </CheckPanel>
  </div>;
}
